import type { AxiosError } from 'axios';

import type { ApiError, ApiErrorResponse } from '../../types/api-error';
import type {
  ApiRequestConfig,
  ApiRequestError,
} from '../../types/api-request-error';

function buildFullUrl(config: Record<string, unknown>): string | undefined {
  const url = config.url as string | undefined;
  const baseURL = config.baseURL as string | undefined;

  if (!url) return undefined;

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  if (!baseURL) return url;

  const base = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
  const path = url.startsWith('/') ? url : `/${url}`;

  return `${base}${path}`;
}

/**
 * Mapeia AxiosError para ApiRequestError (tipo padronizado).
 * Único ponto de acoplamento com Axios - ao trocar de lib, altere apenas aqui.
 * Garante que requestConfig.url tenha a URL completa para matching de expected errors.
 */
export const mapAxiosErrorToApiRequestError = (
  axiosError: AxiosError
): ApiRequestError => {
  const response = axiosError.response;
  const config = axiosError.config as Record<string, unknown> | undefined;

  const fullUrl = config
    ? (buildFullUrl(config) ?? (config.url as string | undefined))
    : undefined;
  const skipToastError = config?.skipToastError as boolean | undefined;
  const requestConfig: ApiRequestConfig | undefined = config
    ? {
        method: (config.method as string | undefined)?.toUpperCase(),
        url: fullUrl,
        skipToastError: skipToastError ?? undefined,
        customErrorTitle: config.customErrorTitle as string | undefined,
        customErrorMessage: config.customErrorMessage as string | undefined,
        expectedErrors: config.expectedErrors as unknown[] | undefined,
      }
    : undefined;

  return {
    message: axiosError.message ?? 'Unknown error',
    code: axiosError.code,
    response: response
      ? {
          data: response.data,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers as Record<string, unknown>,
        }
      : undefined,
    requestConfig,
    originalError: axiosError,
    isApiRequestError: true,
  };
};

/**
 * Mapeia AxiosError para ApiError (legado).
 * Cada adapter tem seu próprio mapper para o erro nativo do cliente.
 */
export const mapAxiosErrorToApiError = (
  axiosError: AxiosError<ApiErrorResponse>
): ApiError => {
  const status = axiosError.response?.status ?? 0;

  const statusText = axiosError.response?.statusText ?? 'Unknown Error';

  const response = axiosError.response?.data;

  const error = Object.create(Error.prototype) as ApiError;

  error.message =
    response?.message ?? response?.title ?? axiosError.message ?? statusText;

  error.name = 'ApiError';

  Object.defineProperties(error, {
    status: { value: status, writable: false },
    statusText: { value: statusText, writable: false },
    response: { value: response, writable: false },
    originalError: { value: axiosError, writable: false },
    isApiError: { value: true, writable: false },
  });

  return error;
};
